import {
  Box,
  FormControl,
  FormControlLabel,
  FormHelperText,
  InputAdornment,
  InputLabel,
  MenuItem,
  Modal,
  Radio,
  RadioGroup,
  Select,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { Controller, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { useEffect, useState } from "react";
import { useGetCities } from "src/api/generic";
import { useRecoilState } from "recoil";
import { notificationsState } from "src/store/notifications";
import { LoadingButton } from "@mui/lab";
import { useTranslation } from "react-i18next";
import CircleOutlinedIcon from "@mui/icons-material/CircleOutlined";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import { useGetAllPackages, useUpdateCompanyMutation } from "src/api/add-sales";
import { DatePicker } from "@mui/x-date-pickers";
import moment from "moment";
import PackagePopup from "./select-package";

interface IModel {
  open: boolean;
  companyId: number;
}

const schema = yup.object().shape({
  company_name: yup.string().required("Required"),
  branch_name: yup.string().required("Required"),
  city_id: yup.number().required("Branch location is required"),
  has_vat: yup.string().required("You have to select an option"),
  start_balance_date: yup.date().required("Required"),

  vat: yup.string().when("has_vat", {
    is: "yes",
    then: (value) => value.required("Required"),
  }),
});

interface FormInputs extends yup.InferType<typeof schema> {}

const FirstBranchPopup = (props: IModel) => {
  const { open, companyId } = props;
  const [notifications, setNotifications] = useRecoilState(notificationsState);
  const [currentStep, setCurrentStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const { t } = useTranslation("addSales");
  const { t: generalT } = useTranslation("general");
  const [selectedPackage, setSelectedPackage] = useState("");

  const { data: citiesData } = useGetCities();
  const { mutate, status, error } = useUpdateCompanyMutation(companyId);
  const { data, isFetching } = useGetAllPackages();

  const {
    control,
    formState: { errors },
    watch,
    getValues,
    trigger,
  } = useForm<FormInputs>({
    mode: "onBlur",
    resolver: yupResolver(schema),
  });

  // Go to previous step
  const goBack = () => {
    if (currentStep > 0) {
      setCurrentStep((prev) => prev - 1);
    }
  };

  const goNext = async () => {
    const validStep = await trigger(
      [
        "company_name",
        "branch_name",
        "city_id",
        "has_vat",
        "start_balance_date",
        "vat",
      ] as any[],
      {
        shouldFocus: true,
      },
    );
    //not valid from
    if (!validStep) {
      return;
    }
    if (currentStep === 0) {
      setCurrentStep((prev) => prev + 1);
      return;
    }
    const data = getValues();
    const {
      company_name,
      branch_name,
      city_id,
      has_vat,
      vat,
      start_balance_date,
    } = data;

    const companyDataToSend = {
      company: {
        name: company_name,
        start_balance_date: moment(start_balance_date).format("YYYY-MM-DD"),
      },
      branch: {
        name: branch_name,
        city_id: +city_id,
        has_vat: has_vat === "yes",
        vat: has_vat === "yes" ? (vat ? Number(vat) / 100 : 0) : 0,
      },
      subscription: {
        package_id: selectedPackage,
      },
    };

    setLoading(true);
    mutate(companyDataToSend);
  };

  //Add success and error handling
  useEffect(() => {
    if (status === "success") {
      const delayedExecution = setTimeout(() => {
        window.location.reload();
        setNotifications([
          ...notifications,
          {
            type: "success",
            message: generalT("addedSuccessfully"),
          },
        ]);
      }, 8000);
      return () => clearTimeout(delayedExecution);
    } else if (status === "error") {
      setLoading(false);
      setNotifications([
        ...notifications,
        {
          type: "error",
          message: error?.data.errors?.[0].value || generalT("somethingWrong"),
        },
      ]);
    }
  }, [status]);

  return (
    <Modal
      open={open}
      // onClose={handleClose}
      aria-labelledby="modal-modal-title"
      aria-describedby="modal-modal-description"
    >
      <Stack
        spacing={3}
        sx={{
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          minWidth: 537,
          bgcolor: "#FFF",
          border: "1px solid #D0D5DD",
          borderRadius: 1,
          boxShadow: 24,
          p: 3,
          maxHeight: "90vh",
          overflow: "auto",
          maxWidth: "100vw",
        }}
      >
        <Box
          display={"flex"}
          justifyContent={"space-between"}
          alignItems={"center"}
          p={0}
        >
          <Typography color={"#101828"} fontSize={"20px"}>
            {currentStep === 0
              ? t("addCompanyDetails")
              : "Add package for this branch"}
          </Typography>
        </Box>
        <form>
          {currentStep === 0 ? (
            <Stack spacing={2}>
              <Controller
                name="company_name"
                control={control}
                render={({ field }) => (
                  <TextField
                    label={t("companyName")}
                    fullWidth
                    size="small"
                    {...field}
                    error={!!errors.company_name}
                    helperText={errors.company_name?.message}
                  />
                )}
              />
              <Controller
                name="branch_name"
                control={control}
                render={({ field }) => (
                  <TextField
                    label={generalT("branchName")}
                    fullWidth
                    size="small"
                    {...field}
                    error={!!errors.branch_name}
                    helperText={errors.branch_name?.message}
                  />
                )}
              />
              <FormControl fullWidth size="small" error={!!errors.city_id}>
                <InputLabel id="demo-simple-select-label">
                  {generalT("branchLocation")}
                </InputLabel>
                <Controller
                  name="city_id"
                  control={control}
                  render={({ field }) => (
                    <Select
                      {...field}
                      key={field.value}
                      labelId="demo-simple-select-label"
                      id="demo-simple-select"
                      label={generalT("branchLocation")}
                    >
                      {citiesData?.data?.cities?.map(({ id, name }) => {
                        return (
                          <MenuItem key={id} value={`${id}`}>
                            {name}
                          </MenuItem>
                        );
                      })}
                    </Select>
                  )}
                />
                <FormHelperText id="my-helper-text">
                  {errors.city_id?.message}
                </FormHelperText>
              </FormControl>
              <Stack
                direction={"row"}
                alignItems={"center"}
                gap={{ xs: 1, sm: 1, md: 3 }}
                flexWrap={"wrap"}
                width={"100%"}
              >
                <Typography color={"textSecondary"}>
                  {t("areYourRegistered")}
                </Typography>
                <FormControl
                  fullWidth
                  size="small"
                  error={!!errors.has_vat}
                  sx={{ width: "fit-content", ml: { xs: "auto", sm: "unset" } }}
                >
                  <Controller
                    name="has_vat"
                    control={control}
                    render={({ field }) => (
                      <RadioGroup
                        {...field}
                        key={field.value}
                        row
                        aria-labelledby="demo-row-radio-buttons-group-label"
                        name="row-radio-buttons-group"
                      >
                        <FormControlLabel
                          value="yes"
                          control={
                            <Radio
                              icon={<CircleOutlinedIcon />}
                              checkedIcon={<CheckCircleIcon />}
                            />
                          }
                          label={generalT("yes")}
                          sx={{ color: "#98A2B3" }}
                        />
                        <FormControlLabel
                          value="no"
                          control={
                            <Radio
                              icon={<CircleOutlinedIcon />}
                              checkedIcon={<CheckCircleIcon />}
                            />
                          }
                          label={generalT("no")}
                          sx={{ color: "#98A2B3" }}
                        />
                      </RadioGroup>
                    )}
                  />
                </FormControl>
              </Stack>
              <FormHelperText error id="my-helper-text" sx={{ px: 2 }}>
                {errors.has_vat?.message}
              </FormHelperText>

              {watch("has_vat") === "yes" && (
                <Controller
                  name="vat"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      type="number"
                      label={`${generalT("vat")} %`}
                      {...field}
                      error={!!errors.vat}
                      helperText={errors.vat?.message}
                      fullWidth
                      size="small"
                      InputLabelProps={{
                        shrink: field.value !== undefined,
                      }}
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="start">%</InputAdornment>
                        ),
                      }}
                    />
                  )}
                />
              )}

              {/* start balance date */}
              <Controller
                name="start_balance_date"
                control={control}
                render={({ field }) => (
                  <DatePicker
                    {...field}
                    slotProps={{
                      textField: {
                        fullWidth: true,
                        error: !!errors.start_balance_date,
                        helperText: t(
                          `${errors.start_balance_date?.message ?? ""}`,
                        ),
                        size: "small",
                        inputProps: {
                          readOnly: true,
                          onKeyDownCapture: (e) => {
                            e.preventDefault();
                            e.stopPropagation();
                          },
                          onPasteCapture: (e) => {
                            e.preventDefault();
                            e.stopPropagation();
                          },
                        },
                      },
                    }}
                    format={"dd/MM/yyyy"}
                    label={`${generalT("startBalanceDate")}`}
                  />
                )}
              />
            </Stack>
          ) : (
            <PackagePopup
              selectedPackage={selectedPackage}
              setSelectedPackage={setSelectedPackage}
              packagesList={data?.data ?? []}
              isFetching={isFetching}
            />
          )}

          <Stack mt={3} spacing={2} direction={"row"}>
            <LoadingButton
              variant="outlined"
              color="tertiary"
              fullWidth
              onClick={goBack}
            >
              {generalT("back")}
            </LoadingButton>
            <LoadingButton
              loading={loading}
              variant="contained"
              fullWidth
              onClick={goNext}
            >
              {generalT(currentStep === 0 ? "next" : "save")}
            </LoadingButton>
          </Stack>
        </form>
      </Stack>
    </Modal>
  );
};

export default FirstBranchPopup;
